import { type ServerResponse, axios } from '~/axios'
import { type CurrentSurvey } from '~/utils/types/current-survey'
import {
  type LockSurveyFromSurveyBuilderResponse,
  type SurveyIsLockedFromSurveyBuilderResponse,
  type UnlockSurveyFromSurveyBuilderResponse,
} from '~/utils/types/survey-builder'
import { type StoplightColorValue } from '~/utils/types/surveys'

type SuggestedChangeStatus = 'APPROVED' | 'REJECTED'

export async function listStoplightsNotes(id: number) {
  interface Note {
    id: number
    note: string
    surveyStoplight: string
    createdBy: string
    status: string
    createdAt: string
    updatedAt: string
  }
  interface ListNotesByIndicatorResponse {
    listNotesByIndicator: Note[]
  }

  const query = /* GraphQL */ `
    query listNotesByIndicator($id: Long) {
      listNotesByIndicator(id: $id) {
        id
        note
        surveyStoplight
        createdBy
        status
        createdAt
        updatedAt
      }
    }
  `

  return await axios<ServerResponse<ListNotesByIndicatorResponse>>({
    method: 'post',
    url: '/graphql',
    data: { query, variables: { id } },
  })
}

export async function addStoplightNote(surveyStoplightNoteModel: {
  note: string
  surveyStoplight: number
}) {
  const query = /* GraphQL */ `
    mutation addSurveyStoplightNote(
      $surveyStoplightNoteModel: SurveyStoplightNoteModelInput
    ) {
      addSurveyStoplightNote(
        surveyStoplightNoteModel: $surveyStoplightNoteModel
      ) {
        successful
      }
    }
  `
  return await axios<ServerResponse<{ successful: boolean }>>({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: {
        surveyStoplightNoteModel,
      },
    },
  })
}

export async function editStoplightNote(surveyStoplightNoteModel: {
  id: number
  note: string
}) {
  const query = /* GraphQL */ `
    mutation updateSurveyStoplightNote(
      $surveyStoplightNoteModel: SurveyStoplightNoteModelInput
    ) {
      updateSurveyStoplightNote(
        surveyStoplightNoteModel: $surveyStoplightNoteModel
      ) {
        successful
      }
    }
  `
  return await axios<ServerResponse<{ successful: boolean }>>({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: {
        surveyStoplightNoteModel,
      },
    },
  })
}

export async function deleteStoplightNote(id: number) {
  const query = /* GraphQL */ `
    mutation deleteSurveyStoplightNote($id: Long) {
      deleteSurveyStoplightNote(id: $id) {
        successful
      }
    }
  `
  return await axios<ServerResponse<{ successful: boolean }>>({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: {
        id,
      },
    },
  })
}

export async function resolveStoplightNotes(id: number) {
  const query = /* GraphQL */ `
    mutation markAllResolvedByIndicator($id: Long) {
      markAllResolvedByIndicator(id: $id) {
        successful
      }
    }
  `
  return await axios<ServerResponse<{ successful: boolean }>>({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: {
        id,
      },
    },
  })
}
export interface BuilderSurveyByID {
  title: string
  id: number
  createdAt: string
  status: string
  description: string
  minimumPriorities: number
  lang: string
  locked?: any
  stoplightType: string
  privacyPolicy: { title: string; text: string }
  termsConditions: { title: string; text: string }
  surveyConfig: SurveyConfig
  surveyEconomicQuestions: SurveyEconomicQuestion[]
  surveyStoplightQuestions: SurveyStoplightQuestion[]
}

interface SurveyConfig {
  documentType: DocumentType[]
  gender: DocumentType[]
  stoplightOptional: null
  signSupport: null
  pictureSupport: null
  surveyLocation: SurveyLocation
}

interface DocumentType {
  text: string
  value: string
  otherOption: boolean
  // TODO: type this
  conditions?: any[]
}

interface SurveyLocation {
  country: string
  latitude: number
  longitude: number
}

interface SurveyEconomicQuestion {
  questionText: string
  codeName: string
  answerType: string
  shortName: string
  topic: string
  topicAudio: null
  required: boolean
  forFamilyMember: boolean
  forTableau: boolean
  orderNumber: number
  introduction: null
  options: DocumentType[]
  // TODO: type this
  conditions: any[]
  // TODO: type this
  conditionGroups: any[]
}

export interface SurveyStoplightQuestion {
  questionText: string
  definition: string
  shortName: string
  description: string
  codeName: string
  dimension: string
  id: number
  surveyStoplightDimension: { id: number }
  surveyIndicator: { codeName: string }
  questionAudio: null
  stoplightColors: StoplightColor[]
  required: boolean
  orderNumber: number
  reviewed: boolean | null
  status: string
  enableDoesNotApply: boolean
  // TODO: type this
  suggestedIndicatorChanges: any[]
}

export interface StoplightColor {
  url: string
  urlImage: string
  value: number
  description: string
  // TODO: type this
  suggestedIndicatorColorChanges: any[]
}

export async function getSurveyForBuilder(surveyId: number) {
  return await axios.post<ServerResponse<{ surveyById: BuilderSurveyByID }>>(
    '/graphql',
    JSON.stringify({
      query: `query { surveyById(surveyId:${surveyId}) { customLabels {labelText labelType} lifemapQuestions{questionText questionType} title id createdAt status description minimumPriorities lang stoplightType privacyPolicy { title  text } termsConditions{ title text }  surveyConfig { disableMemberQuestions isDemo isSurveyAnonymous shouldCreateUserBeforeSurveyFlow verifiedEmailRequired documentType {text value otherOption } gender { text value otherOption } stoplightOptional signSupport pictureSupport surveyLocation { country latitude longitude} } surveyEconomicQuestions { questionText codeName answerType shortName topic topicAudio required forFamilyMember forTableau orderNumber introduction options {text value otherOption conditions{codeName, type, values, operator, valueType, showIfNoData}}, conditions{codeName, type, value, operator}, conditionGroups{groupOperator, joinNextGroup, conditions{codeName, type, value, operator}} } surveyStoplightQuestions { questionText definition shortName description codeName dimension id surveyStoplightDimension{id} surveyIndicator{codeName} questionAudio stoplightColors { url urlImage value description suggestedIndicatorColorChanges{ id suggestedColumn suggestedChange } } required orderNumber reviewed status enableDoesNotApply suggestedIndicatorChanges{ id suggestedColumn suggestedChange } } } }`,
    }),
  )
}

export async function addSuggestedChanges(
  // TODO: Type this object
  survey: any,
) {
  return await axios({
    method: 'post',
    url: '/graphql',
    data: JSON.stringify({
      query:
        'mutation addSuggestedChangesForIndicators( $surveyDefinition : SurveyDefinitionModelInput, $stoplightIndicators:[SurveyStoplightModelInput]) {addSuggestedChangesForIndicators ( surveyDefinition:$surveyDefinition,  stoplightIndicators:$stoplightIndicators) {successful}}',
      variables: {
        ...survey,
      },
    }),
  })
}

export async function approveOrRejectIndicatorChange(
  suggestedIndicatorChangeId: number,
  status: SuggestedChangeStatus,
) {
  return await axios({
    method: 'post',
    url: '/graphql',
    data: JSON.stringify({
      query:
        'mutation approveOrRejectIndicatorChange( $suggestedIndicatorChangeId : Long, $status:String) {approveOrRejectIndicatorChange ( suggestedIndicatorChangeId:$suggestedIndicatorChangeId,  status:$status) {successful}}',
      variables: {
        suggestedIndicatorChangeId,
        status,
      },
    }),
  })
}

export async function approveOrRejectIndicatorColorChange(
  suggestedIndicatorColorChangeId: number,
  status: SuggestedChangeStatus,
) {
  return await axios({
    method: 'post',
    url: '/graphql',
    data: JSON.stringify({
      query:
        'mutation approveOrRejectIndicatorColorChange( $suggestedIndicatorColorChangeId : Long, $status:String) {approveOrRejectIndicatorColorChange ( suggestedIndicatorColorChangeId:$suggestedIndicatorColorChangeId,  status:$status) {successful}}',
      variables: {
        suggestedIndicatorColorChangeId,
        status,
      },
    }),
  })
}

export async function listAllIndicators(filter: string, language: string) {
  interface StoplightIndicator {
    codeName: string
    definition: string
    description: string
    id: number
    questionText: string
    shortName: string
    stoplightColors: StoplightIndicatorColor[]
    stoplightDimension: { name: string }
  }

  interface StoplightIndicatorColor {
    url: string
    description: string
    value: StoplightColorValue
  }

  const query = /* GraphQL */ `
    query listAllStoplightIndicator($lang: String, $filter: String) {
      listAllStoplightIndicator(lang: $lang, filter: $filter) {
        id
        codeName
        questionText
        shortName
        description
        definition
        stoplightDimension {
          name
        }
        stoplightColors {
          value
          description
          url
        }
      }
    }
  `

  return await axios<
    ServerResponse<{
      listAllStoplightIndicator: StoplightIndicator[]
    }>
  >({
    method: 'post',
    url: '/graphql',
    data: { query, variables: { lang: language, filter } },
  })
}

export async function lockSurveyFromSurveyBuilder(surveyDefinitionId: number) {
  const response = await axios.post<LockSurveyFromSurveyBuilderResponse>(
    '/graphql',
    {
      query: `mutation lockSurveyBuilder($surveyDefinitionId: Long) { lockSurveyBuilder(surveyDefinitionId: $surveyDefinitionId) { successful } }`,
      variables: {
        surveyDefinitionId,
      },
    },
  )

  return response.data.data.lockSurveyBuilder
}

export async function unlockSurveyFromSurveyBuilder(
  surveyDefinitionId: number,
) {
  const response = await axios.post<UnlockSurveyFromSurveyBuilderResponse>(
    '/graphql',
    {
      query: `mutation unlockSurveyBuilder($surveyDefinitionId: Long) { unlockSurveyBuilder(surveyDefinitionId: $surveyDefinitionId) { successful } }`,
      variables: {
        surveyDefinitionId,
      },
    },
  )

  return response.data.data.unlockSurveyBuilder
}

export async function checkIfSurveyIsLockedFromSurveyBuilder(
  surveyDefinitionId: number,
) {
  const response = await axios.post<SurveyIsLockedFromSurveyBuilderResponse>(
    '/graphql',
    {
      query: `query isSurveyLocked($surveyDefinitionId: Long) { isSurveyLocked(surveyDefinitionId: $surveyDefinitionId) { isLocked lockedBy lockedByUserId } }`,
      variables: {
        surveyDefinitionId,
      },
    },
  )

  return response.data.data.isSurveyLocked
}

export async function createSurveyDefinition(survey: Partial<CurrentSurvey>) {
  return await axios.post<
    ServerResponse<{ createSurveyDefinition: { id: number } }>
  >(
    '/graphql',
    JSON.stringify({
      query:
        'mutation createSurveyDefinition($privacyPolicy: TermCondPolDTOInput, $termCond : TermCondPolDTOInput, $surveyDefinition : SurveyDefinitionModelInput, $application: Long) { createSurveyDefinition (privacyPolicy: $privacyPolicy, termCond:$termCond, surveyDefinition:$surveyDefinition, application:$application) { id, title, description, countryCode,latitude,longitude,lang, privacyPolicy{title,text}, termsConditions{title,text}}  }',
      variables: {
        ...survey,
      },
    }),
  )
}

export async function updateSurveyDefinition(survey: Partial<CurrentSurvey>) {
  const query = /* GraphQL */ `
    mutation updateSurveyDefinitionFromBuilder(
      $privacyPolicy: TermCondPolDTOInput
      $termCond: TermCondPolDTOInput
      $surveyDefinition: SurveyDefinitionModelInput
      $economicQuestions: [SurveyEconomicModelInput]
      $surveyConfig: SurveyConfigInput
      $stoplightIndicators: [SurveyStoplightModelInput]
    ) {
      updateSurveyDefinitionFromBuilder(
        privacyPolicy: $privacyPolicy
        termCond: $termCond
        surveyDefinition: $surveyDefinition
        economicQuestions: $economicQuestions
        surveyConfig: $surveyConfig
        stoplightIndicators: $stoplightIndicators
      ) {
        successful
      }
    }
  `

  return await axios.post<
    ServerResponse<{
      updateSurveyDefinitionFromBuilder: { successful: boolean }
    }>
  >('/graphql', {
    query,
    variables: { ...survey },
  })
}

export async function supportedLanguages() {
  interface SupportedLanguagesResponse {
    code: string
    description: string
  }

  const query = /* GraphQL */ `
    query {
      supportedLanguages {
        code
        description
      }
    }
  `

  return await axios<
    ServerResponse<{ supportedLanguages: SupportedLanguagesResponse[] }>
  >({
    method: 'post',
    url: '/graphql',
    data: { query },
  })
}

export async function surveysByUserPaginated(
  filters: {
    filter: string
    country: string
    sortBy: string
    sortDirection: string
    lang: string | null
    hubId?: number | null
    organizationId?: number | null
    pageSize?: number | null
  },
  page: number,
) {
  const query = /* GraphQL */ `
    query surveysByUserPaginated(
      $filter: String
      $country: String
      $page: Int
      $sortBy: String
      $sortDirection: String
      $lang: String
      $hubId: Long
      $organizationId: Long
      $pageSize: Int
    ) {
      surveysByUserPaginated(
        filter: $filter
        country: $country
        page: $page
        sortBy: $sortBy
        sortDirection: $sortDirection
        lang: $lang
        hubId: $hubId
        organizationId: $organizationId
        pageSize: $pageSize
      ) {
        content {
          id
          title
          indicatorsCount
          createdAt
          updatedAt
          status
          organizations {
            id
            name
          }
          applications {
            id
            name
          }
          assignedUsers {
            userId
            username
            role
          }
          locked
          lockedBy
          lockedByUserId
          anonymousSurvey
        }
        totalPages
        totalElements
      }
    }
  `

  interface SurveysByUserPaginated {
    content: SurveyItem[]
    totalPages: number
    totalElements: number
  }

  interface SurveyItem {
    id: number
    title: string
    indicatorsCount: number
    createdAt: string
    updatedAt: Date
    status: 'APPROVED' | 'IN_REVIEW'
    applications: Array<{ id: number; name: string }>
    organizations: Array<{ id: number; name: string }>
    assignedUsers: AssignedUser[]
    locked: boolean
    lockedBy: string
    lockedByUserId: number
    anonymousSurvey: boolean
  }

  interface AssignedUser {
    userId: number
    username: string
    role: string
  }

  return await axios<
    ServerResponse<{
      surveysByUserPaginated: SurveysByUserPaginated
    }>
  >({
    method: 'post',
    url: '/graphql',
    data: {
      query,
      variables: {
        ...filters,
        page,
      },
    },
  })
}
